import React, { useState } from "react";
import {
  TextFieldComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";

interface TextFieldProps {
  config: TextFieldComponent;
  value?: string;
  onChange?: (name: string, value: string) => void;
  externalError?: string;
}

export const TextField: React.FC<TextFieldProps> = ({
  config,
  value = "",
  onChange,
  externalError,
}) => {
  const [error, setError] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);

  const validateField = (inputValue: string): string => {
    // Check required property first (from IsMandatory)
    if (config.required && !inputValue.trim()) {
      return `${config.label || "This field"} is required`;
    }

    // Then check validation rules if they exist
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (!inputValue.trim())
            return rule.message || "This field is required";
          break;
        case ValidationRule.MIN_LENGTH:
          if (inputValue.length < (rule.value as number))
            return rule.message || `Minimum ${rule.value} characters required`;
          break;
        case ValidationRule.MAX_LENGTH:
          if (inputValue.length > (rule.value as number))
            return rule.message || `Maximum ${rule.value} characters allowed`;
          break;
        case ValidationRule.EMAIL:
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(inputValue))
            return rule.message || "Please enter a valid email address";
          break;
        case ValidationRule.PATTERN:
          const regex = new RegExp(rule.value as string);
          if (!regex.test(inputValue)) return rule.message || "Invalid format";
          break;
      }
    }
    return "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const validationError = validateField(newValue);
    setError(validationError);
    onChange?.(config.name, newValue);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const validationError = validateField(value);
    setError(validationError);
  };

  const getInputType = () => {
    switch (config.inputType) {
      case "email":
        return "email";
      case "password":
        return "password";
      case "number":
        return "number";
      case "phone":
        return "tel";
      case "url":
        return "url";
      default:
        return "text";
    }
  };

  const getWidthClass = () => {
    if (config.style?.widthMode === "wrap") return "w-auto";
    return "w-full";
  };

  const inputStyles: React.CSSProperties = {
    borderRadius: config.style?.borderRadius || 8,
    fontSize: config.style?.fontSize || 14, // Standardized to 14px (text-sm)
    fontWeight: config.style?.fontWeight || "normal",
    // Only set color/backgroundColor if explicitly provided AND not in dark mode, otherwise let Tailwind handle it
    // In dark mode, always use light text for visibility
    ...(config.style?.textColor && !config.disabled && !document.documentElement.classList.contains('dark') ? { color: config.style.textColor } : {}),
    ...(config.style?.backgroundColor && !config.disabled && !document.documentElement.classList.contains('dark') ? { backgroundColor: config.style.backgroundColor } : {}),
    textAlign: config.style?.textAlign || "left",
    fontFamily: "inherit", // Use system font stack
  };

  return (
    <div className={`${getWidthClass()} px-2`} data-field-name={config.name}>
      {config.label && (
        <label className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <input
        type={getInputType()}
        name={config.name}
        placeholder={config.placeholder}
        value={value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        disabled={config.disabled}
        maxLength={config.maxLength}
        className={`w-full p-2 px-4 text-sm font-normal text-left border border-1 outline-none transition-colors duration-200 font-sans bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white dark:!text-white placeholder-gray-400 dark:placeholder-gray-500 ${config.disabled ? "cursor-not-allowed opacity-70 dark:opacity-100 bg-gray-100 dark:bg-gray-700 dark:!text-white" : "cursor-text"
          } ${error
            ? "border-danger-500 dark:border-danger-500"
            : isFocused
              ? "border-primary-500 dark:border-primary-400"
              : "border-gray-300 dark:border-gray-600"
          } focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
        style={inputStyles}
      />

      {(error || externalError) && (
        <div className="text-danger-500 dark:text-danger-400 text-sm mt-1 flex items-center">
          <span className="mr-1">⚠️</span>
          {error || externalError}
        </div>
      )}

      {config.helperText && !error && !externalError && (
        <div className="text-gray-500 dark:text-gray-400 text-sm mt-1">{config.helperText}</div>
      )}
    </div>
  );
};
